import {
  Box,
  Collapse,
  Fade,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useAuthContext } from "../../hooks/Auth";
import { postChatSofia } from "../../services/api";
import UmmIAPediaCardImg from "../../components/UmmIApps/Cards/ChatExpertoCardImg.png";
import HeaderApps from "../../components/UmmIACategories/Layout/HeaderApps";
import MarkDownText from "../../components/MarkDownText/MarkDownText";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useActividadesContext } from "../../components/UmmIACategories/Hook/useActividades";

import FileUpload, { IDocumento } from "../../components/utils/FileUpload";
import AttachedFileChip from "../../components/utils/AttachedFileChip";
import AttachedFileChipReadOnly from "../../components/utils/AttachedFileChipReadOnly";

const nombreChatAsistente = "Ummia";

type ChatMessage = {
  message: string;
  sentTime: string;
  sender: string; 
  direction?: "" | "outgoing";
  documento?: IDocumento; 
};

// Tipo de mensajes para el historial SIN 'documento'
type ChatMessageHistory = Omit<ChatMessage, "documento">;

function ChatBedrockPage() {
  const currentTime = new Date();
  const formattedTime = format(currentTime, "hh:mm aa");
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      message: `Hola soy ${nombreChatAsistente}. ¿En qué puedo ayudarte?`,
      sentTime: formattedTime,
      sender: nombreChatAsistente,
      direction: "",
    },
  ]);

  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [showHeader, setShowHeader] = useState(true);
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null);

  // Input de mensaje
  const [inputValue, setInputValue] = useState("");

  // promptData del contexto
  const { promptData, setPromptData } = useActividadesContext();

  const handleBack = () => {
    navigate("/asistente/ummiapedia");
  };

  const handleSend = async () => {
    if (inputValue.trim() === "") return;

    const now = format(new Date(), "hh:mm aa");

    const newInputMessage: ChatMessage = {
      message: inputValue,
      sender: "user",
      direction: "outgoing",
      sentTime: now,
      // Si hay adjunto en el área de entrada, lo incluimos en el mensaje
      documento: promptData?.documento,
    };

    if (showHeader) setShowHeader(false);

    const newMessages = [...messages, newInputMessage];
    setMessages(newMessages);

    // Limpia el campo de texto
    setInputValue("");

    // Limpia el adjunto de la zona de entrada
    if (promptData?.documento) {
      setPromptData((prev: any) => {
        const next = { ...prev };
        delete next.documento;
        return next;
      });
    }

    setTyping(true);
    await processMessageToApi(newMessages);
  };

  async function processMessageToApi(chatMessages: ChatMessage[]) {
    try {
      // Mensaje más reciente (con 'documento' disponible si lo hubo)
      const lastMessage = chatMessages[chatMessages.length - 1];

      // Historial SIN la clave 'documento'
      const messagesForHistory: ChatMessageHistory[] = chatMessages.map(
        ({ documento, ...rest }) => rest
      );
      const historial = { chatMessages: messagesForHistory };

      const payload = {
        usuario: user,
        contenidos: {
          temperatura: 0.7,
          modelID: 14,
          prompt: lastMessage.message,
          // Adjuntamos el archivo SOLO aquí (si existe)
          documento: lastMessage.documento
            ? {
                name: lastMessage.documento.name,
                type: lastMessage.documento.type,
                size: lastMessage.documento.size,
                lastModified: lastMessage.documento.lastModified,
                extension: lastMessage.documento.extension,
                contentBase64: lastMessage.documento.contentBase64,
              }
            : undefined,
        },
        historial, 
      };

      const response = await postChatSofia(payload);

      if (response.status === 200) {
        const data = response.data;
        const newFormattedTime = format(new Date(), "hh:mm aa");

        const newMessages: ChatMessage[] = [
          ...chatMessages,
          {
            message: data.respuesta,
            sender: nombreChatAsistente,
            sentTime: newFormattedTime,
          },
        ];

        setMessages(newMessages);
        setTyping(false);
      } else {
        console.error(
          `Error al llamar a la API: ${response.status} ${response.statusText}`
        );
        setTyping(false);
      }
    } catch (error: any) {
      console.error("Error general:", error.message);
      setTyping(false);
    }
  }

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  // Cargar archivo: guarda en promptData.documento y NO crea mensaje del sistema
  const handleFileLoaded = (doc: IDocumento) => {
    setPromptData((prev: any) => ({
      ...prev,
      documento: doc,
    }));
    if (showHeader) setShowHeader(false);
  };

  // Eliminar archivo adjunto (zona de entrada)
  const handleRemoveFile = () => {
    setPromptData((prev: any) => {
      const next = { ...prev };
      delete next.documento;
      return next;
    });
  };

  return (
    <>
      {/* Cabecera */}
      <Collapse in={showHeader} timeout={500}>
        <HeaderApps
          imageSrc={UmmIAPediaCardImg}
          title="Chat experto"
          titleColor="#8B3355"
          description="Conversa con una IA entrenada con contenido pedagógico y didáctico"
          backgroundColorImage="linear-gradient(360deg, #A25C77 0%, #E8D6DD 50%, #FCFCFC 100%)"
          onBack={handleBack}
          colorIconBack="#8B3355"
        />
      </Collapse>

      <Fade in={!showHeader} timeout={400}>
        <IconButton
          onClick={handleBack}
          sx={{
            position: "fixed",
            bgcolor: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(4px)",
            border: "1px solid #ddd",
            boxShadow: 2,
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            transition: "transform .25s",
            "&:active": { transform: "scale(0.92)" },
          }}
        >
          <ArrowBackIosNewIcon sx={{ color: "#8B3355" }} />
        </IconButton>
      </Fade>

      {/* Cuerpo del Chat */}
      <Grid item xs={12}>
        <Grid
          sx={{
            mt: 4,
            height: showHeader ? "50vh" : "82vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "10px",
            fontFamily: "Montserrat",
            overflowY: "auto",
          }}
        >
          {/* Área de mensajes */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  backgroundColor: msg.direction === "outgoing" ? "#DCE4E9" : "#F6E6C4",
                  p: 1,
                  borderRadius: "10px",
                  width: "fit-content",
                  maxWidth:
                    msg.direction === "outgoing"
                      ? { xs: "80%", sm: "60%", md: "50%" }
                      : { xs: "90%", sm: "70%", md: "60%" },
                  alignSelf:
                    msg.direction === "outgoing" ? "flex-end" : "flex-start",
                  //Esto para que no desborde
                  "& p, & ul, & ol, & h1, & h2, & h3, & h4, & h5, & h6": {
                    marginBlockStart: 0,
                    marginBlockEnd: 0,
                  },
                  "& *": {
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  },
                }}
              >
                {/* Si este mensaje tiene adjunto, mostramos el chip solo lectura */}
                {msg.documento && (
                  <Box sx={{ mb: 0.5 }}>
                    <AttachedFileChipReadOnly documento={msg.documento} />
                  </Box>
                )}

                <MarkDownText data={msg.message} img={""} />

                <Typography
                  variant="caption"
                  sx={{
                    color: "#9e9e9e",
                    display: "block",
                    textAlign: "right",
                  }}
                >
                  {msg.sentTime} - {msg.sender}
                </Typography>
              </Box>
            ))}
            {typing && (
              <Box sx={{ alignSelf: "flex-start", mb: 1 }}>
                <Typography variant="body2" sx={{ fontStyle: "italic", color: "#424242" }}>
                  Ummia está escribiendo...
                </Typography>
              </Box>
            )}
            <Grid ref={endOfMessagesRef} />
          </Box>

          {/* Input de mensaje + Adjuntar + Chip del archivo (con eliminar) */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              pb: 10,
            }}
          >
           <FileUpload
              label="Adjuntar"
              variant="outlined"
              onLoaded={handleFileLoaded}
              maxSizeMB={5}
            />

            {promptData?.documento && <AttachedFileChip documento={promptData.documento} onRemove={handleRemoveFile} />}

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ingrese un mensaje..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              sx={{
                backgroundColor: "#E8E8E8",
                borderRadius: 5,
                fontFamily: "Montserrat",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default ChatBedrockPage;
